import React, { useContext, useEffect, useMemo, useState } from 'react';

import { tryCatch } from '@thalesrc/js-utils';
import { useTranslation } from 'react-i18next';

import { ToasterContext } from './ToasterContext';
import { UserContext } from './UserContext';
import { PERMISSION_CODES } from '../containers/roleManagement/const';
import { MODULE_CODES_AND_PATH, ROLES } from '../enums/const';
import { moduleService } from '../services/module';
import pageURL from '../utils/pageUrls';

export const MenuContext = React.createContext();

export function MenuProvider(props) {
  const { t } = useTranslation();

  const [menu, setMenu] = useState([]);

  const { token, hasAccess, permissions } = useContext(UserContext);
  const { openToaster } = useContext(ToasterContext);

  const canSeeRoles = hasAccess(PERMISSION_CODES.ROLE_LIST);
  const canSeeUserRoles = hasAccess(PERMISSION_CODES.ROLE_USER_ROLE_LIST);
  const canSeeGroups = hasAccess(PERMISSION_CODES.GROUP_LIST);

  useEffect(() => {
    if (token) {
      getModules();
    }
  }, [token, permissions]);

  const getModules = async () => {
    const [err, res] = await tryCatch(moduleService.getAll());
    if (err) {
      openToaster('bottom-right', {
        severity: 'error',
        summary: t('general.error'),
        detail: err?.response?.data?.errors?.[0],
        life: 3000
      });
      return;
    }

    createMenuStructure(res);
  };

  const ecomLinkCreator = (ecomModule) => {
    if (!ecomModule) return null;
    const isWatsonsOrAdmin =
      ecomModule.role?.code === ROLES.SYS_ADMIN ||
      ecomModule.role?.code === ROLES.ECOM_WATSONS_ADMIN ||
      ecomModule.role?.code === ROLES.ECOM_WATSONS_USER;

    return {
      label: ecomModule.name,
      icon: 'pi pi-shopping-bag',
      to: isWatsonsOrAdmin
        ? pageURL.productDetailInformationManagement
        : pageURL.supplierProductDetailInformationManagement
    };
  };

  const getMenus = (modules, isManagement) => {
    return modules
      ?.filter((item) => item.isManagement === isManagement)
      .map((item) => {
        const module = MODULE_CODES_AND_PATH.find((mod) => mod.code === item.code);
        if (module) {
          if (item.code === 'ECOM') {
            return ecomLinkCreator(item);
          }
          if (item.code === 'ROLE' && !(canSeeRoles || canSeeUserRoles || canSeeGroups)) {
            return null;
          }
          return {
            label: item.name,
            icon: module.icon,
            to: module.to
          };
        }
        return null;
      })
      .filter((item) => item !== null);
  };

  const createMenuStructure = (modules) => {
    const mainMenu = getMenus(modules, false);
    const administrationMenu = getMenus(modules, true);

    const menuStructure = [];

    if (mainMenu?.length > 0) {
      menuStructure.push({
        label: t('general.mainMenu'),
        to: '/',
        items: mainMenu,
        dataTestId: 'main-menu-group'
      });
    }
    if (administrationMenu?.length > 0) {
      menuStructure.push({
        label: t('general.administrationModules'),
        to: '/',
        items: administrationMenu,
        dataTestId: 'administration-menu-group'
      });
    }

    // Temporary solution to remove duplicate items from the menu, this happens due to user having multiple roles in the same module.
    // Remove this once we have a proper solution to handle multiple roles in the same module.
    const deduplicatedMenu = menuStructure.map((section) => ({
      ...section,
      items: [...new Map(section.items.map((item) => [item.to, item])).values()]
    }));

    setMenu(deduplicatedMenu);
  };

  const value = useMemo(() => ({ menu, setMenu, getModules }), [menu, setMenu, getModules]);

  return <MenuContext.Provider value={value}>{props.children}</MenuContext.Provider>;
}
